/**
 * TicketDetailContent — corpo reutilizável do detalhe do ticket.
 *
 * Extraído de TicketDetailPage (#150) para ser consumido por DOIS lugares:
 *  - `TicketDetailPage` (rota `/helpdesk/tickets/:id`, deep-link)
 *  - `TicketDetailDrawer` (clique no card do board → slide-over)
 *
 * Recebe `ticketId` por prop (em vez de `useParams`). Toda a lógica de ações
 * (pause/resume/close/reopen/transfer/tags/etc.) vive aqui.
 */

import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';

import { useApp } from '@/contexts/AppContext';
import { useStagesByPipelineId } from '@/modules/helpdesk/pipelines/hooks/useStages';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { ChecklistEntityTab } from '@/modules/checklists/components/ChecklistEntityTab';
import { DrawerTabBar } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { ResolveTicketModal } from './ResolveTicketModal';
import { StageChangeConfirmation } from './StageChangeConfirmation';
import { TicketDetailHeader } from './TicketDetailHeader';
import { TicketEditor } from './TicketEditor';
import { TicketSidebarCards } from './sidebar/TicketSidebarCards';
import type { SidebarTeam, SidebarUser } from './sidebar/TicketSidebarCards';
import { TicketTimelineV2 } from './timeline/TicketTimelineV2';
import { useTicketById, useUpdateTicket, useCloseTicket, useReopenTicket, useTransferTicket } from '../hooks';
import type { TicketPriority, TicketStageResponse, TicketStatusCategory, UpdateTicketRequest } from '../types';
import { getApiErrorMessage } from '../utils';

interface TicketDetailContentProps {
  ticketId: string;
  /**
   * `compact` força o layout de coluna única com abas Atividades|Detalhes
   * (o mesmo do mobile) INDEPENDENTE da largura do viewport. Usado pelo
   * `TicketDetailDrawer` (960px) — sem isso o `lg:` do viewport abre as duas
   * colunas dentro do drawer, e a coluna de cards fica presa em 318px
   * desperdiçando o resto do painel.
   */
  compact?: boolean;
  /**
   * Presente = hospedado num drawer: o header vira `variant="drawer"` (ícone +
   * badge do id + título/ações/X na MESMA barra — DS 2026-08-11) e os estados
   * de loading/erro ganham uma barra mínima com X pra nunca deixar o usuário
   * sem fechar (Esc segue funcionando via `Drawer`).
   */
  onCloseDrawer?: () => void;
  /**
   * `isOpen` REAL do drawer hospedeiro (`TicketDetailDrawer`) — gateia a aba
   * Checklists (CLAUDE.md SEMPRE #10). Na página completa (`TicketDetailPage`,
   * sem drawer) o componente só monta quando a rota está ativa, então o
   * default `true` já reflete isso corretamente.
   */
  isOpen?: boolean;
}

/** Barra mínima só com o X — usada nos estados de loading/erro dentro do drawer,
 * antes do `TicketDetailHeader` existir. */
const DrawerCloseBar: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className="flex-none flex items-center justify-end px-3 h-11 border-b" style={{ borderColor: '#eef1f5' }}>
    <button
      onClick={onClose}
      className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
      style={{ color: '#64748b' }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
      aria-label="Fechar"
      data-testid="ticket-detail-drawer-close"
    >
      <Icon name="x" className="w-4 h-4" />
    </button>
  </div>
);

export const TicketDetailContent: React.FC<TicketDetailContentProps> = ({ ticketId, compact = false, onCloseDrawer, isOpen = true }) => {
  // Buscar dados do ticket
  const { data: ticket, isLoading, error } = useTicketById(ticketId);

  // Buscar stages do pipeline do ticket
  const { data: stages = [] } = useStagesByPipelineId(ticket?.pipelineId || undefined);

  // Users and teams for sidebar
  const { users: appUsers } = useApp();
  const { data: teamsData = [] } = useTeamsDropdown();

  const sidebarUsers: SidebarUser[] = useMemo(
    () =>
      appUsers.map((u) => ({
        id: u.id,
        name: `${u.firstName} ${u.lastName}`.trim(),
        picture: u.avatar,
      })),
    [appUsers],
  );

  const sidebarTeams: SidebarTeam[] = useMemo(() => teamsData.map((t) => ({ id: t.id, name: t.name })), [teamsData]);

  // Mutations
  const updateMutation = useUpdateTicket();
  const closeMutation = useCloseTicket();
  const reopenMutation = useReopenTicket();
  const transferMutation = useTransferTicket();

  // Stage change confirmation state (reusada pelo header E pela sidebar)
  const [stageConfirmation, setStageConfirmation] = useState<{
    stageId: string;
    stage: TicketStageResponse;
  } | null>(null);

  // Modal do Resolver (fluxo dedicado — Task 5/9)
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);

  // Mobile: abas "Atividades | Detalhes | Checklists"
  const [mobileTab, setMobileTab] = useState<'activities' | 'details' | 'checklists'>('activities');

  // Helper: filtrar stages por categoria
  const filterStagesByCategory = (category: TicketStatusCategory): TicketStageResponse[] => {
    return stages?.filter((s) => s.category === category) || [];
  };

  // Handlers de ações
  const handlePause = async (stageId?: string): Promise<void> => {
    if (!ticket) return;
    try {
      const targetStageId = stageId || filterStagesByCategory('waiting')[0]?.id;
      if (!targetStageId) {
        toast.error('Nenhuma etapa de pausa encontrada');
        return;
      }
      await updateMutation.mutateAsync({ id: ticket.id, stageId: targetStageId });
      toast.success('Ticket pausado');
    } catch {
      toast.error('Erro ao pausar ticket');
    }
  };

  const handleResume = async (stageId?: string): Promise<void> => {
    if (!ticket) return;
    try {
      const targetStageId = stageId || filterStagesByCategory('in_progress')[0]?.id;
      if (!targetStageId) {
        toast.error('Nenhuma etapa em andamento encontrada');
        return;
      }
      await updateMutation.mutateAsync({ id: ticket.id, stageId: targetStageId });
      toast.success('Ticket retomado');
    } catch {
      toast.error('Erro ao retomar ticket');
    }
  };

  const handleClose = async (stageId?: string): Promise<void> => {
    if (!ticket) return;
    try {
      if (stageId) {
        await updateMutation.mutateAsync({ id: ticket.id, stageId });
      } else {
        await closeMutation.mutateAsync(ticket.id);
      }
      toast.success('Ticket fechado');
    } catch {
      toast.error('Erro ao fechar ticket');
    }
  };

  const handleReopen = async (stageId?: string): Promise<void> => {
    if (!ticket) return;
    try {
      if (stageId) {
        await updateMutation.mutateAsync({ id: ticket.id, stageId });
      } else {
        await reopenMutation.mutateAsync(ticket.id);
      }
      toast.success('Ticket reaberto');
    } catch {
      toast.error('Erro ao reabrir ticket');
    }
  };

  const handleTitleChange = async (title: string): Promise<void> => {
    if (!ticket) return;
    try {
      await updateMutation.mutateAsync({ id: ticket.id, title });
      toast.success('Título atualizado!');
    } catch {
      toast.error('Erro ao atualizar título');
    }
  };

  const handleStageChange = async (stageId: string): Promise<void> => {
    if (!ticket) return;
    try {
      await updateMutation.mutateAsync({ id: ticket.id, stageId });
      toast.success('Etapa atualizada!');
    } catch {
      toast.error('Erro ao atualizar etapa');
    }
  };

  const handlePriorityChange = async (priority: TicketPriority): Promise<void> => {
    if (!ticket) return;
    try {
      await updateMutation.mutateAsync({ id: ticket.id, priority });
      toast.success('Prioridade atualizada!');
    } catch {
      toast.error('Erro ao atualizar prioridade');
    }
  };

  const handleStageChangeWithConfirmation = (stageId: string, stage: TicketStageResponse): void => {
    setStageConfirmation({ stageId, stage });
  };

  const handleConfirmStageChange = async (): Promise<void> => {
    if (!stageConfirmation || !ticket) return;
    try {
      await updateMutation.mutateAsync({ id: ticket.id, stageId: stageConfirmation.stageId });
      toast.success('Etapa alterada');
      setStageConfirmation(null);
    } catch {
      toast.error('Erro ao alterar etapa');
      setStageConfirmation(null);
    }
  };

  const handleAssigneeChange = async (assigneeId: string | null): Promise<void> => {
    if (!ticket) return;
    try {
      await transferMutation.mutateAsync({ ticketId: ticket.id, body: { toUserId: assigneeId } });
      toast.success(assigneeId ? 'Responsável atualizado!' : 'Responsável removido!');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erro ao atualizar responsável'));
    }
  };

  const handleTeamChange = async (teamId: string | null): Promise<void> => {
    if (!ticket) return;
    try {
      await transferMutation.mutateAsync({ ticketId: ticket.id, body: { toTeamId: teamId } });
      toast.success(teamId ? 'Equipe atualizada!' : 'Equipe removida!');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erro ao atualizar equipe'));
    }
  };

  const handleAddTag = async (tag: string): Promise<void> => {
    if (!ticket) return;
    try {
      const newTags = [...(ticket.tags || []), tag];
      await updateMutation.mutateAsync({ id: ticket.id, tags: newTags });
      toast.success('Tag adicionada!');
    } catch {
      toast.error('Erro ao adicionar tag');
    }
  };

  const handleRemoveTag = async (tag: string): Promise<void> => {
    if (!ticket || !ticket.tags) return;
    try {
      const newTags = ticket.tags.filter((t) => t !== tag);
      await updateMutation.mutateAsync({ id: ticket.id, tags: newTags });
      toast.success('Tag removida!');
    } catch {
      toast.error('Erro ao remover tag');
    }
  };

  const handleDescriptionUpdate = async (data: Partial<UpdateTicketRequest>): Promise<void> => {
    if (!ticket) return;
    try {
      await updateMutation.mutateAsync({ id: ticket.id, ...data });
      toast.success('Ticket atualizado!');
    } catch {
      toast.error('Erro ao atualizar ticket');
    }
  };

  const handleCustomerChange = async (customerId: string): Promise<void> => {
    if (!ticket) return;
    try {
      await updateMutation.mutateAsync({ id: ticket.id, customerId });
      toast.success(customerId ? 'Cliente atualizado!' : 'Cliente removido!');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erro ao atualizar cliente'));
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        {onCloseDrawer && <DrawerCloseBar onClose={onCloseDrawer} />}
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-lg" style={{ backgroundColor: 'var(--theme-border)' }} />
            <div className="h-4 w-32 rounded" style={{ backgroundColor: 'var(--theme-border)' }} />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !ticket) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        {onCloseDrawer && <DrawerCloseBar onClose={onCloseDrawer} />}
        <div className="flex-1 flex items-center justify-center">
          <div
            className="px-6 py-4 rounded-lg border"
            style={{
              backgroundColor: 'var(--theme-error-background)',
              borderColor: 'var(--theme-error-border)',
              color: 'var(--theme-error-text)',
            }}
          >
            {error ? 'Erro ao carregar ticket' : 'Ticket não encontrado'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Header — variant drawer quando hospedado no TicketDetailDrawer */}
      <TicketDetailHeader
        ticket={ticket}
        onPause={handlePause}
        onResume={handleResume}
        onResolveClick={() => setIsResolveModalOpen(true)}
        onClose={handleClose}
        onReopen={handleReopen}
        onTitleChange={handleTitleChange}
        onStageChangeWithConfirmation={handleStageChangeWithConfirmation}
        stages={stages || []}
        loading={updateMutation.isPending || closeMutation.isPending || reopenMutation.isPending}
        variant={onCloseDrawer ? 'drawer' : 'page'}
        onCloseDrawer={onCloseDrawer}
      />

      {/* Abas Detalhes | Atividades — DrawerTabBar shared (padrão DS 2026-08-11:
          abas com largura do conteúdo, Detalhes primeiro, como o drawer de lead).
          Sempre visíveis no compact (drawer); no page só no mobile (some no lg,
          onde as 2 colunas convivem). */}
      <div
        className={`${compact ? '' : 'lg:hidden'} flex-none`}
        style={{ backgroundColor: 'var(--theme-card-background)' }}
      >
        <DrawerTabBar
          items={[
            { id: 'details', label: 'Detalhes', icon: 'info', testid: 'ticket-detail-mtab-details' },
            { id: 'activities', label: 'Atividades', icon: 'message-circle', testid: 'ticket-detail-mtab-activities' },
            { id: 'checklists', label: 'Checklists/Formulários', icon: 'check-square', testid: 'ticket-detail-mtab-checklists' },
          ]}
          active={mobileTab}
          onChange={(id) => setMobileTab(id as 'activities' | 'details' | 'checklists')}
        />
      </div>

      {/* Main Content — timeline central + composer fixo (T1) */}
      <div className="flex-1 flex overflow-hidden">
        <div
          className={`${
            mobileTab === 'activities' ? 'flex' : 'hidden'
          } ${compact ? '' : 'lg:flex'} flex-1 min-w-0 flex-col overflow-hidden`}
        >
          <div className="flex-1 overflow-y-auto">
            <TicketTimelineV2 ticketId={ticket.id} />
          </div>

          <div
            className="flex-shrink-0 border-t p-4"
            style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
          >
            <TicketEditor ticketId={ticket.id} ticketTitle={ticket.title} isEmailTicket={Boolean(ticket.emailChannelId)} />
          </div>
        </div>

        {/* Sidebar — cards T1 (mobile/compact = aba Detalhes) */}
        <div
          className={`${mobileTab === 'details' ? 'flex' : 'hidden'} ${
            compact ? '' : 'lg:flex'
          } flex-1 ${compact ? '' : 'lg:flex-none'} min-w-0 overflow-hidden`}
        >
          <TicketSidebarCards
            compact={compact}
            ticket={ticket}
            stages={stages || []}
            users={sidebarUsers}
            teams={sidebarTeams}
            onUpdate={handleDescriptionUpdate}
            onStageChange={handleStageChange}
            onPriorityChange={handlePriorityChange}
            onAssigneeChange={handleAssigneeChange}
            onTeamChange={handleTeamChange}
            onCustomerChange={handleCustomerChange}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
            onStageChangeWithConfirmation={handleStageChangeWithConfirmation}
            loading={updateMutation.isPending || transferMutation.isPending}
          />
        </div>

        {/* Checklists (Task 17) — mesma aba do drawer de lead, componente ÚNICO.
            Só existe no compact (drawer)/mobile: no page desktop (`lg`, não
            compact) a barra de abas some (`lg:hidden` acima) e as 2 colunas
            (timeline + sidebar) ficam sempre visíveis — sem espaço de tab pra
            um 3º painel ali.
            Montagem CONDICIONAL (não CSS-only) — Fix Round 1 da review: os
            outros 2 painéis (timeline/sidebar) ficam sempre montados porque
            no desktop full-page eles convivem lado a lado (nenhum dos dois é
            "opcional"); Checklists é estritamente 1-aba-por-vez, então
            `ChecklistEntityTab` (e os fetches `by-entity`/users-dropdown que
            ele dispara ao montar) só existe no DOM quando o usuário
            efetivamente abre esta aba — mesmo mecanismo do drawer de lead
            (`{activeTab === 'checklists' && <ChecklistEntityTab .../>}`). O
            `lg:hidden` sobrevive pra cobrir o caso de resize (usuário abre a
            aba no mobile e depois alarga a janela pra desktop, no page
            full — não-compact). */}
        {mobileTab === 'checklists' && (
          <div className={`flex flex-1 min-w-0 flex-col overflow-hidden ${compact ? '' : 'lg:hidden'}`}>
            <div className="flex-1 overflow-y-auto p-4">
              <ChecklistEntityTab entityType="ticket" entityId={ticket.id} isOpen={isOpen} />
            </div>
          </div>
        )}
      </div>

      {/* Stage Change Confirmation */}
      {stageConfirmation &&
        ticket &&
        (() => {
          const currentStage = stages.find((s) => s.id === ticket.stageId);
          return (
            <StageChangeConfirmation
              isOpen={!!stageConfirmation}
              onClose={() => setStageConfirmation(null)}
              onConfirm={handleConfirmStageChange}
              fromStageName={currentStage?.name || ticket.status || '—'}
              toStageName={stageConfirmation.stage.name}
              fromCategory={(currentStage?.category || ticket.statusCategory) as TicketStatusCategory}
              toCategory={stageConfirmation.stage.category as TicketStatusCategory}
              isLoading={updateMutation.isPending}
            />
          );
        })()}

      {/* Resolver — fluxo dedicado */}
      <ResolveTicketModal isOpen={isResolveModalOpen} onClose={() => setIsResolveModalOpen(false)} ticket={ticket} />
    </div>
  );
};
